import {
  Illustration,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LoadingSpinner,
  mergeSxProps,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";

import { HowGettingPaidWorksBottomSheet } from "./HowGettingPaidWorksBottomSheet";
import { StripeSetupTipsBottomSheet } from "./StripeSetupTipsBottomSheet";

export interface StripeOnboardingIntroPageContentProps {
  isSetUpStripeLoading: boolean;
  setUpStripe: () => void;
  sx?: SxProps<Theme>;
}

export function StripeOnboardingIntroPageContent(props: StripeOnboardingIntroPageContentProps) {
  const { isSetUpStripeLoading, setUpStripe, sx } = props;
  const howGettingPaidWorksModalState = useModalState();
  const stripeSetupTipsModalState = useModalState();

  return (
    <Stack
      sx={mergeSxProps(
        (theme) => ({
          flex: 1,
          paddingX: theme.spacing(5),
          paddingTop: theme.spacing(10),
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(6) }),
        }),
        sx
      )}
    >
      <Stack spacing={9}>
        <Stack spacing={9} sx={{ alignItems: "center" }}>
          <Illustration type="clipboard-plus-stripe" />
          <Text semibold variant="h4" sx={{ textAlign: "center" }}>
            Clipboard partners with Stripe for payments
          </Text>
        </Stack>

        <List disablePadding sx={(theme) => ({ marginTop: theme.spacing(8) })}>
          <ListItem>
            <ListItemIcon iconType="bolt" />
            <ListItemText
              primary={
                <Text semibold variant="h6">
                  Get paid in minutes
                </Text>
              }
              secondary={
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  Enable InstantPay to get your money in 30 minutes, or use free Standard payouts
                  (1-3 business days).
                </Text>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon iconType="locked" />
            <ListItemText
              primary={
                <Text semibold variant="h6">
                  Get paid securely
                </Text>
              }
              secondary={
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  Payments are sent securely through Stripe to your bank/card after your shift ends.
                </Text>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon iconType="coin" />
            <ListItemText
              primary={
                <Text semibold variant="h6">
                  No taxes are withheld
                </Text>
              }
              secondary={
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  If you earn $600+ per year, you&apos;ll receive a 1099-NEC form on Stripe for tax
                  filing.
                </Text>
              }
            />
          </ListItem>
        </List>
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Stack sx={{ alignItems: "center" }} spacing={4}>
        <Button
          fullWidth
          disabled={isSetUpStripeLoading}
          onClick={stripeSetupTipsModalState.openModal}
        >
          {isSetUpStripeLoading ? <LoadingSpinner size="small" /> : "Set up Stripe account"}
        </Button>
        <TextButton onClick={howGettingPaidWorksModalState.openModal}>
          Learn more about getting paid
        </TextButton>
      </Stack>
      <HowGettingPaidWorksBottomSheet modalState={howGettingPaidWorksModalState} />
      <StripeSetupTipsBottomSheet modalState={stripeSetupTipsModalState} onClosed={setUpStripe} />
    </Stack>
  );
}
